/**
 * SegmentedControl - pick exactly one of a few short options, all visible.
 *
 * Atomic Design role: Molecule. A row of joined pill buttons with radio
 * semantics (role="radiogroup" / role="radio"), for a choice small enough
 * that a dropdown would only hide it — "CSR / JSR / Full User" on the Quick
 * Add User modal is the first user.
 *
 * Keyboard follows the WAI-ARIA radio-group pattern: one tab stop (the
 * selected option), and the arrow keys move AND select, wrapping at the ends.
 *
 * Controlled: the parent owns `value`.
 *
 * @example
 * <SegmentedControl
 *     label="Type"
 *     options={[{ value: 'csr', label: 'CSR' }, { value: 'jsr', label: 'JSR' }]}
 *     value={type}
 *     onChange={setType}
 * />
 */
import React, { useRef } from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface SegmentedControlOption<T extends string = string> {
	/** Value handed to onChange. */
	value: T;
	/** Text on the segment. Keep it to a word or two. */
	label: string;
	/** Optional tooltip / accessible description for the segment. */
	description?: string;
}

export interface SegmentedControlProps<T extends string = string> {
	/** Visible label above the control; also names the radiogroup. */
	label: string;
	/** The choices, in display order. */
	options: SegmentedControlOption<T>[];
	/** Currently selected value. */
	value: T;
	/** Called with the newly selected value. */
	onChange: (value: T) => void;
	/** Disables every segment. */
	disabled?: boolean;
	/** Optional extra classes on the root. */
	className?: string;
	/** Overrides the molecule's data-ref so a composing component keeps its identity. */
	dataRef?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export function SegmentedControl<T extends string = string>({ label, options, value, onChange, disabled = false, className = '', dataRef = 'molecule-segmented-control' }: SegmentedControlProps<T>) {
	/* Refs to each segment so arrow keys can move focus along with the selection. */
	const buttonRefs = useRef<Array<HTMLButtonElement | null>>([]);

	/** Arrow-key navigation: move + select, wrapping at either end. */
	const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
		const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
		if (step === 0) return;
		e.preventDefault();
		const next = (index + step + options.length) % options.length;
		onChange(options[next].value);
		buttonRefs.current[next]?.focus();
	};

	return (
		<div className={`segmented-control ${className}`.trim()} data-ref={dataRef}>
			<span className="block text-14px font-sans font-medium text-gray-700 mb-4px">{label}</span>
			<div role="radiogroup" aria-label={label} className="segmented-control__group inline-flex w-full rounded-md border border-brand-border bg-brand-surface p-2px">
				{options.map((option, index) => {
					const selected = option.value === value;
					return (
						<button
							key={option.value}
							ref={(el) => { buttonRefs.current[index] = el; }}
							type="button"
							role="radio"
							aria-checked={selected}
							/* Roving tabindex: only the selected segment is a tab stop. */
							tabIndex={selected ? 0 : -1}
							title={option.description}
							disabled={disabled}
							onClick={() => onChange(option.value)}
							onKeyDown={(e) => handleKeyDown(e, index)}
							className={`segmented-control__option flex-1 min-h-44px desktop:min-h-36px px-12px rounded-[5px] text-14px font-sans font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary disabled:opacity-50 disabled:cursor-not-allowed ${selected ? 'bg-white text-brand-primary shadow-sm' : 'bg-transparent text-brand-text-muted hover:text-brand-text'}`}
						>
							{option.label}
						</button>
					);
				})}
			</div>
		</div>
	);
}

export default SegmentedControl;
